import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { SectionTitle, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Factory } from "lucide-react";
import { toast } from "sonner";

function productionStatus(s) {
  return s === "completed" || s === "in_progress" ? s : "assigned";
}

export default function Production() {
  const [items, setItems] = useState([]);
  const [target, setTarget] = useState(null);
  const [qty, setQty] = useState("");
  const [mos, setMos] = useState([]);
  const [moOpen, setMoOpen] = useState(false);
  const [moForm, setMoForm] = useState({ fg_sku: "", quantity: "", warehouse: "", remarks: "", materials: [{ sku: "", quantity: "", _key: Date.now() }] });
  const [recvMo, setRecvMo] = useState(null);
  const [recvForm, setRecvForm] = useState({ output_qty: "", rejected_qty: "0", scrap_qty: "0", remarks: "" });
  const { user } = useAuth();
  const canManageMo = ["admin", "warehouse_manager", "production", "supervisor"].includes(user?.role);
  const navigate = useNavigate();
  const canReceive = ["admin", "warehouse_manager", "production"].includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/production-requests");
    setItems(res.data);
    api.get("/production/orders").then((r) => setMos(r.data)).catch(() => setMos([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const receive = async () => {
    try {
      await api.post(`/orders/${target.bill_no}/production-received`, { quantity: parseInt(qty) || 0 });
      toast.success("FG receipt recorded — stock updated");
      setTarget(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const createMo = async () => {
    try {
      await api.post("/production/orders", {
        fg_sku: moForm.fg_sku, quantity: parseInt(moForm.quantity) || 0,
        warehouse: moForm.warehouse || null, remarks: moForm.remarks || null,
        materials: moForm.materials.filter((m) => m.sku && m.quantity).map((m) => ({ sku: m.sku, quantity: parseInt(m.quantity) || 0 })),
      });
      toast.success("Production order created");
      setMoOpen(false);
      setMoForm({ fg_sku: "", quantity: "", warehouse: "", remarks: "", materials: [{ sku: "", quantity: "", _key: Date.now() }] });
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
  };

  const moAction = async (mo, action) => {
    try {
      await api.post(`/production/orders/${mo.mo_id}/${action}`);
      toast.success(action === "reserve-materials" ? "Materials reserved" : "Materials issued — RM stock reduced");
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
  };

  const receiveMo = async () => {
    try {
      await api.post(`/production/orders/${recvMo.mo_id}/receive`, {
        output_qty: parseInt(recvForm.output_qty) || 0,
        rejected_qty: parseInt(recvForm.rejected_qty) || 0,
        scrap_qty: parseInt(recvForm.scrap_qty) || 0,
        remarks: recvForm.remarks || null,
      });
      toast.success("FG received — staging updated, putaway task auto-created");
      setRecvMo(null);
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="production-page">
      <SectionTitle right={canManageMo && (
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setMoOpen(true)} data-testid="new-mo-button">+ Production Order</Button>
      )}>Production Requirements</SectionTitle>
      <p className="text-sm text-muted-foreground">Auto-created when an order's stock check finds a shortage. When FG is received into the warehouse, stock and the linked order update automatically.</p>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="production-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["PR No", "Order", "SKU", "Required", "Shortage", "Received", "Status", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.pr_id} className="border-b border-border/50" data-testid={`pr-row-${p.pr_id}`}>
                <td className="px-4 py-3 metric font-bold text-primary">{p.pr_id}</td>
                <td className="px-4 py-3"><button className="metric text-signal-blue underline" onClick={() => navigate(`/orders/${p.bill_no}`)}>{p.bill_no}</button></td>
                <td className="px-4 py-3">{p.item_name} <span className="metric text-xs text-muted-foreground">({p.sku})</span></td>
                <td className="px-4 py-3 metric">{p.required_qty.toLocaleString()}</td>
                <td className="px-4 py-3 metric text-signal-red font-bold">{p.shortage_qty.toLocaleString()}</td>
                <td className="px-4 py-3 metric">{(p.received_qty || 0).toLocaleString()}</td>
                <td className="px-4 py-3"><StatusBadge status={productionStatus(p.status)} /></td>
                <td className="px-4 py-3 metric text-xs">{fmtDT(p.created_at)}</td>
                <td className="px-4 py-3">
                  {p.status !== "completed" && canReceive && (
                    <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => { setTarget(p); setQty(String(p.shortage_qty - (p.received_qty || 0))); }} data-testid={`receive-${p.pr_id}`}>
                      <Factory className="w-3.5 h-3.5 mr-1" /> FG Received
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No production requirements</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="space-y-3" data-testid="mo-section">
        <h3 className="text-sm font-semibold uppercase tracking-wide">Production Orders (RM Issue → FG Receipt)</h3>
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="mo-table">
            <thead><tr className="border-b border-border text-left">
              {["MO No", "FG SKU", "Planned", "Route / Stages", "Materials (issued/required)", "Output", "Rejected", "Scrap", "Variance", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {mos.map((m) => (
                <tr key={m.mo_id} className="border-b border-border/50" data-testid={`mo-row-${m.mo_id}`}>
                  <td className="px-4 py-3 metric font-bold text-primary">{m.mo_id}</td>
                  <td className="px-4 py-3 text-xs">{m.fg_name || m.fg_sku} <span className="metric text-muted-foreground">({m.fg_sku})</span></td>
                  <td className="px-4 py-3 metric">{m.quantity.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap" data-testid={`mo-stages-${m.mo_id}`}>
                      {(m.stages || []).map((s) => (
                        <span key={s.seq} title={s.stage} className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${s.status === "completed" ? "bg-signal-green/15 text-signal-green" : s.status === "qc_failed" ? "bg-signal-red/15 text-signal-red" : s.status === "task_created" ? "bg-signal-blue/15 text-signal-blue" : "bg-muted text-muted-foreground"}`}>
                          {s.stage.slice(0, 4)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{m.materials.map((x) => `${x.sku}: ${x.issued_qty}/${x.required_qty}`).join(" · ") || "—"}</td>
                  <td className="px-4 py-3 metric">{m.output_qty ?? "—"}</td>
                  <td className="px-4 py-3 metric text-signal-red">{m.rejected_qty ?? "—"}</td>
                  <td className="px-4 py-3 metric">{m.scrap_qty ?? "—"}</td>
                  <td className="px-4 py-3 metric">{m.variance_qty ?? "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {m.status === "pending" && m.materials.length > 0 && canManageMo && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => moAction(m, "reserve-materials")} data-testid={`mo-reserve-${m.mo_id}`}>Reserve RM</Button>
                      )}
                      {["pending", "material_reserved"].includes(m.status) && m.materials.length > 0 && canManageMo && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => moAction(m, "issue")} data-testid={`mo-issue-${m.mo_id}`}>Issue RM</Button>
                      )}
                      {["in_progress", "ready_for_receipt"].includes(m.status) && canManageMo && (() => {
                        const stagesDone = !m.stages?.length || m.stages.every((s) => s.status === "completed");
                        const blocked = m.status === "in_progress" && m.stages?.length && !stagesDone;
                        return (
                          <span title={blocked ? "All process stages must complete first" : ""}>
                            <Button size="sm" className="bg-signal-green hover:bg-signal-green/90 text-white h-7 text-xs"
                              disabled={!!blocked}
                              onClick={() => { setRecvMo(m); setRecvForm({ output_qty: String(m.quantity), rejected_qty: "0", scrap_qty: "0", remarks: "" }); }}
                              data-testid={`mo-receive-${m.mo_id}`}>Receive FG</Button>
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
              {mos.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">No production orders — create one to run RM issue → FG receipt with variance tracking</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={moOpen} onOpenChange={setMoOpen}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="mo-create-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Production Order</DialogTitle><DialogDescription className="sr-only">Create a production order with raw material requirements</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>FG SKU *</Label><Input data-testid="mo-fg-sku" value={moForm.fg_sku} onChange={(e) => setMoForm({ ...moForm, fg_sku: e.target.value.toUpperCase() })} className="font-mono" /></div>
              <div><Label>Planned Qty *</Label><Input data-testid="mo-qty" type="number" value={moForm.quantity} onChange={(e) => setMoForm({ ...moForm, quantity: e.target.value })} /></div>
              <div><Label>Warehouse</Label><Input data-testid="mo-warehouse" value={moForm.warehouse} onChange={(e) => setMoForm({ ...moForm, warehouse: e.target.value })} /></div>
              <div><Label>Remarks</Label><Input data-testid="mo-remarks" value={moForm.remarks} onChange={(e) => setMoForm({ ...moForm, remarks: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Raw Materials (SKU + qty per batch)</Label>
              {moForm.materials.map((m, i) => (
                <div key={m._key || i} className="flex gap-2">
                  <Input className="font-mono flex-1" placeholder="RM SKU" data-testid={`mo-rm-sku-${i}`} value={m.sku}
                    onChange={(e) => setMoForm({ ...moForm, materials: moForm.materials.map((x, xi) => xi === i ? { ...x, sku: e.target.value.toUpperCase() } : x) })} />
                  <Input className="w-28" type="number" placeholder="Qty" data-testid={`mo-rm-qty-${i}`} value={m.quantity}
                    onChange={(e) => setMoForm({ ...moForm, materials: moForm.materials.map((x, xi) => xi === i ? { ...x, quantity: e.target.value } : x) })} />
                </div>
              ))}
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="mo-add-rm"
                onClick={() => setMoForm({ ...moForm, materials: [...moForm.materials, { sku: "", quantity: "", _key: Date.now() }] })}>+ Add material</Button>
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={createMo} data-testid="mo-create-submit">Create Production Order</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recvMo} onOpenChange={() => setRecvMo(null)}>
        <DialogContent className="bg-card border-border" data-testid="mo-receive-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">FG Receipt — {recvMo?.mo_id}</DialogTitle><DialogDescription className="sr-only">Record production output, rejection and scrap</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Planned: <span className="metric">{recvMo?.quantity}</span> pcs of <span className="metric text-primary">{recvMo?.fg_sku}</span> — output goes to staging and a putaway task is auto-created</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Good output *</Label><Input type="number" data-testid="mo-out-qty" value={recvForm.output_qty} onChange={(e) => setRecvForm({ ...recvForm, output_qty: e.target.value })} /></div>
              <div><Label>Rejected</Label><Input type="number" data-testid="mo-rej-qty" value={recvForm.rejected_qty} onChange={(e) => setRecvForm({ ...recvForm, rejected_qty: e.target.value })} /></div>
              <div><Label>Scrap</Label><Input type="number" data-testid="mo-scrap-qty" value={recvForm.scrap_qty} onChange={(e) => setRecvForm({ ...recvForm, scrap_qty: e.target.value })} /></div>
            </div>
            <div><Label>Remarks</Label><Input data-testid="mo-recv-remarks" value={recvForm.remarks} onChange={(e) => setRecvForm({ ...recvForm, remarks: e.target.value })} /></div>
            <Button className="w-full bg-signal-green hover:bg-signal-green/90 text-white font-bold" onClick={receiveMo} data-testid="mo-receive-submit">Record FG Receipt</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!target} onOpenChange={() => setTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="receive-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">FG Receipt — {target?.pr_id}</DialogTitle><DialogDescription className="sr-only">Record finished goods received into warehouse stock</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{target?.item_name} for order <span className="metric text-primary">{target?.bill_no}</span></p>
            <div><Label>Quantity received into warehouse</Label><Input type="number" data-testid="receive-qty-input" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
            <Button className="w-full bg-signal-green hover:bg-signal-green/90 text-white font-bold" onClick={receive} data-testid="receive-submit-button">Record Receipt & Update Stock</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
